import { Dialog, Button } from "@mui/material";
import {
  getLineCounter,
  getPolygonCounter,
  startLineAnalysis,
  startPolygonAnalysis
} from "api/layer";
import { ReactComponent as LineSvg } from "assets/line.svg";
import { ReactComponent as PolygonSvg } from "assets/polygon.svg";
import Svg from "components/svg/svg";
import { RightPanel, Option } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { DrawShapeSource } from "helpers/models";
import { MapContext } from "helpers/map";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./analyse-shape-modal.module.scss";
import SelectLayer from "../select-layer/select-layer";
import { buildLineAnalysisParams } from "../shared/line-analysis-params";
import {
  buildNodeCoordinates,
  reportHazardLayerStatistics
} from "helpers/layerStatistics";

const FATHOM_LAYER_ARRAY = [
  "FL_Fluvial_Global_Fathom",
  "FL_Pluvial_Global_Fathom",
  "FL_Coastal_Global_Fathom"
];

function AnalyseShapeModal() {
  const [context, dispatch] = useContext(AppContext);
  const [option, setOption] = useState<Option>(Option.HAZARD);
  const [t] = useTranslation();
  const [fathomValidateStatus, setFathomValidateStatus] = useState(false);
  const [nonFathomValidateStatus, setNonFathomValidateStatus] = useState(false);

  const { AMap, map } = useContext(MapContext);
  const [hazardButtons, setHazardButtons] = useState<any[]>([]);

  const [selectedButtons, setSelectedButtons] = useState<string[][]>([[]]);
  const selectedShape = useMemo(
    () => context.shapes.find((pl) => pl.id === context.selectedShapeId),
    [context.shapes, context.selectedShapeId]
  );

  const isPolygonSelected =
    selectedShape?.source === DrawShapeSource.POLYGON ||
    selectedShape?.source === DrawShapeSource.SHAPE_POLYGON;

  const getDisplayShapeName = () => {
    if (selectedShape) {
      return selectedShape.name;
    }

    return t("drawShape.lineInfoTitle", {
      data: (context.shapes.length ?? 0) + 1
    });
  };

  const onOpen = () => {
    dispatch({
      type: isPolygonSelected ? AppAction.setPolygonSelectLayer : AppAction.setLineSelectLayer,
      payload: selectedButtons
    });
    if (selectedButtons[0].length && !context.shapeAskAgain) {
      const fathomLayer = selectedButtons[0].find((e) =>
        FATHOM_LAYER_ARRAY.includes(e)
      );

      if (fathomLayer) {
        close();
        dispatch({ type: AppAction.setShapeFathomPopup, payload: "line" });
        return;
      }
    }
    runAnalysis();
  };

  const runAnalysis = async (selBtns = selectedButtons) => {
    if (!selectedShape?.aMapRef) {
      console.error("No selected shape");
      return;
    }

    let shapeAmapObj = selectedShape.aMapRef;

    if (!shapeAmapObj.zoom) {
      shapeAmapObj.zoom = Math.floor(map.getZoom());
      dispatch({
        type: AppAction.setShapes,
        payload: context.shapes.map((pl) =>
          pl.id === selectedShape.id ? { ...pl } : pl
        )
      });
    }

    dispatch({
      type: AppAction.setCurrentShape,
      payload: selectedShape
    });

    const params = buildLineAnalysisParams(
      selectedShape,
      map,
      [],
      !!selectedShape.hasShapeAnalysed
    );
    if (!params) {
      return;
    }

    if (selBtns[0].length) {
      params.catNetLayers = selBtns[0];
      const selectedLegends = context.hazardLegends?.filter((item) => {
        return selBtns[0].includes(item.layerId);
      });
      dispatch({
        type: isPolygonSelected
          ? AppAction.setPolygonAnalysisLegends
          : AppAction.setLineAnalysisLegends,
        payload: selectedLegends
      });
    }
    const catNetLayers = params.catNetLayers ?? [];
    if (catNetLayers.length) {
      // 根据形状类型分别设置状态
      if (isPolygonSelected) {
        dispatch({
          type: AppAction.setPolygonAnalysisShapeId,
          payload: selectedShape.id
        });
        dispatch({ type: AppAction.setPolygonExpandedLayerId, payload: undefined });
        dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "running" });
        dispatch({ type: AppAction.setPolygonJobId, payload: undefined });
      } else {
        dispatch({
          type: AppAction.setLineAnalysisShapeId,
          payload: selectedShape.id
        });
        dispatch({ type: AppAction.setLineAnalysisStatus, payload: "running" });
        dispatch({ type: AppAction.setLineJobId, payload: undefined });
      }
      const startTime = Date.now();
      const startAnalysis = isPolygonSelected
        ? startPolygonAnalysis
        : startLineAnalysis;
      startAnalysis(params)
        .then((res: any) => {
          const endTime = Date.now();
          const responseTime = endTime - startTime;
          const nodeCoordinates = buildNodeCoordinates(params.nodes);
          if (!res.code || res.code === 400) {
            reportHazardLayerStatistics({
              eventType: selectedShape.source,
              status: "0",
              layers: catNetLayers,
              locationAmount: 1,
              executeInterval: responseTime,
              coordinates: nodeCoordinates,
              traceInfo: res.message
            });

            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "tip",
                titleIconColor: "red",
                titleIcon: "error",
                resMessage:
                  context.language === "en"
                    ? res.data.messageEN
                    : res.data.messageCN
              }
            });

            dispatch({
              type: AppAction.setSingleLocationAnalysed,
              payload: false
            });
            dispatch({
              type: AppAction.setMultipleLocationsAnalysed,
              payload: false
            });
            return;
          }
          if (res && res.code === 200 && res?.data?.jobId) {
            reportHazardLayerStatistics({
              eventType: selectedShape.source,
              status: "1",
              layers: catNetLayers,
              locationAmount: 1,
              executeInterval: responseTime,
              coordinates: nodeCoordinates
            });

            const containsFathomLayer = catNetLayers.some((layerId) =>
              FATHOM_LAYER_ARRAY.includes(layerId)
            );

            if (containsFathomLayer) {
              const updatedShapes = context.shapes.map((pl) =>
                pl.id === selectedShape.id
                  ? { ...pl, hasShapeAnalysed: true }
                  : pl
              );
              dispatch({ type: AppAction.setShapes, payload: updatedShapes });
              const updatedCurrentShape = updatedShapes.find(
                (pl) => pl.id === selectedShape.id
              );
              if (updatedCurrentShape) {
                dispatch({ type: AppAction.setCurrentShape, payload: updatedCurrentShape });
              }
              if (selectedShape.shapeId) {
                dispatch({
                  type: AppAction.updateMyShape,
                  payload: {
                    id: selectedShape.shapeId,
                    name: updatedCurrentShape?.name || selectedShape.name,
                    type: updatedCurrentShape?.type || selectedShape.type,
                    hasShapeAnalysed: true
                  }
                });
              }
            }

            // 根据形状类型分别设置 jobId
            if (isPolygonSelected) {
              dispatch({ type: AppAction.setPolygonJobId, payload: res.data.jobId });
            } else {
              dispatch({ type: AppAction.setLineJobId, payload: res.data.jobId });
            }
          } else {
            dispatch({ type: AppAction.setJobId, payload: undefined });
            reportHazardLayerStatistics({
              eventType: selectedShape.source,
              status: "0",
              layers: catNetLayers,
              locationAmount: 1,
              executeInterval: responseTime,
              coordinates: nodeCoordinates,
              traceInfo: res.message
            });
            // 根据形状类型分别设置错误状态
            if (isPolygonSelected) {
              dispatch({
                type: AppAction.setPolygonAnalysisStatus,
                payload: "error"
              });
            } else {
              dispatch({
                type: AppAction.setLineAnalysisStatus,
                payload: "error"
              });
            }
          }
        })
        .catch((error) => {
          const endTime = Date.now();
          const responseTime = endTime - startTime;
          dispatch({ type: AppAction.setJobId, payload: undefined });
          reportHazardLayerStatistics({
            eventType: selectedShape.source,
            status: "0",
            layers: catNetLayers,
            locationAmount: 1,
            executeInterval: responseTime,
            coordinates: buildNodeCoordinates(params.nodes),
            traceInfo: error.message
          });
          // 根据形状类型分别设置错误状态
          if (isPolygonSelected) {
            dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "error" });
          } else {
            dispatch({ type: AppAction.setLineAnalysisStatus, payload: "error" });
          }
        });
    }

    dispatch({
      type: AppAction.setRightPanel,
      payload: isPolygonSelected ? RightPanel.Polygon : RightPanel.Line
    });
    close();
  };

  const close = (): void => {
    dispatch({ type: AppAction.setAnalyseShapeModal, payload: undefined });
    handleRevert();
  };

  const handleDistance = () => {
    if (selectedShape?.aMapRef) {
      let path = selectedShape.aMapRef.getPath();
      if (Array.isArray(path?.[0])) {
        path = path[0];
      }

      if (isPolygonSelected) {
        const polygonPath = path.length > 2 ? [...path, path[0]] : path;
        const area = AMap?.GeometryUtil?.ringArea
          ? Math.abs(AMap.GeometryUtil.ringArea(polygonPath))
          : 0;

        if (area < 1000000) {
          return t("map.area(m2)", { data: area.toFixed(2) });
        }

        return t("map.area(km2)", { data: (area / 1000000).toFixed(3) });
      }

      let total = 0;
      for (let i = 0; i < path.length - 1; i++) {
        total += AMap.GeometryUtil.distance(path[i], path[i + 1]);
      }

      if (total < 1000) {
        return t("map.length(m)", { data: total.toFixed(3) });
      } else {
        return t("map.length(km)", { data: (total / 1000).toFixed(3) });
      }
    }
  };

  useEffect(() => {
    if (context.tryAgainType === "lineAnalyse") {
      runAnalysis(context.lineSelectLayer);
    } else if (context.tryAgainType === "polygonAnalyse") {
      runAnalysis(context.polygonSelectLayer);
    }
  }, [context.tryAgainType]);

  useEffect(() => {
    if (context?.layerList?.length) {
      const hazardLegendsIds: string[] = [];
      let hazardPermission = false,
        fathomPermission = false;

      const hazardPermissionId = isPolygonSelected ? "105004" : "105002";
      const fathomPermissionId = isPolygonSelected ? "105003" : "105001";

      hazardPermission = context.menu.find(
        (item) => item.id === hazardPermissionId && item.status === "1"
      );
      fathomPermission = context.menu.find(
        (item) => item.id === fathomPermissionId && item.status === "1"
      );

      let arr =
        context?.layerList
          ?.map((e) => e.children)
          .flat()
          .map((e) => {
            hazardLegendsIds.push(e.id);
            let validateStatus = true;
            let disabled = true;
            let clickDisabled = true;
            if (
              e.id !== "WF_DistToBush_AUS_SwissRe" &&
              e.id !== "DR_Subsidence_France_SwissRe" &&
              e.id !== "FL_Fluvial_Official"
            ) {
              if (e.id.includes("Fathom")) {
                validateStatus = fathomValidateStatus;
                disabled = !fathomPermission || fathomValidateStatus;
              } else {
                validateStatus = nonFathomValidateStatus;
                disabled = !hazardPermission || nonFathomValidateStatus;
              }
              clickDisabled = false;
            }

            return {
              id: e.id,
              title: e.name,
              validateStatus: validateStatus,
              disabled: disabled,
              clickDisabled: clickDisabled,
              icon: Svg({
                icon: e.id,
                disabled: disabled
              })
            };
          }) || [];
      setHazardButtons(arr);
    }
  }, [
    context.menu,
    context.analyseShapeModal,
    context.layerList,
    isPolygonSelected,
    fathomValidateStatus,
    nonFathomValidateStatus
  ]);

  useEffect(() => {
    if (!context.menu?.length) {
      return;
    }

    if (context.analyseShapeModal === undefined) {
      return;
    }
    let locations: any = [];
    if (
      selectedShape?.source === DrawShapeSource.SHAPE_LINE ||
      selectedShape?.source === DrawShapeSource.SHAPE_POLYGON
    ) {
      const myShapeId = selectedShape.shapeId || selectedShape.id;
      if (myShapeId) {
        locations = [myShapeId];
      }
    }

    const getItemCounter = isPolygonSelected ? getPolygonCounter : getLineCounter;

    getItemCounter(
      {
        actionCategories: "fathom,nonFathom",
        isReAnalysis: !!selectedShape?.hasShapeAnalysed
      },
      locations
    ).then((res) => {
      // existInCache 非fathom数据是否存在于缓存中
      // existInEventCache event数据是否存在于缓存中
      // existInFathomCache fathom数据是否存在于缓存中
      setFathomValidateStatus(
        !res.data.existInFathomCache &&
        res.data.itemFathomQuotas !== -1 &&
        (res.data.itemFathomQuotas ?? 0) <= (res.data.trItemFathomCount ?? 0)
      );
      setNonFathomValidateStatus(
        !res.data.existInCache &&
        res.data.itemQuotas !== -1 &&
        (res.data.itemQuotas ?? 0) <= (res.data.trItemCount ?? 0)
      );
    });

    setSelectedButtons([[]]);
    setOption(Option.HAZARD);
  }, [context.menu, context.analyseShapeModal, isPolygonSelected, selectedShape?.hasShapeAnalysed, selectedShape?.id, selectedShape?.source]);

  const handleRevert = () => {
    setSelectedButtons([[]]);
  };

  const isAnySelected = selectedButtons.flatMap((arr) => arr).length;

  return (
    <Dialog
      open={!!context.analyseShapeModal}
      onClose={close}
      sx={{
        "& .MuiDialog-paper": {
          width: "780px",
          maxWidth: "80vw",
          maxHeight: "85vh"
        }
      }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          {isPolygonSelected
            ? t("analyseModal.analyzePolygon")
            : t("analyseModal.analyzeLines")}
        </div>
        <div className={styles.analyse}>
          <div>
            <div className={styles.header}>
              {isPolygonSelected ? (
                <PolygonSvg className={styles.image} />
              ) : (
                <LineSvg className={styles.image} />
              )}
              <div>
                <div className={styles.headerName}>{getDisplayShapeName()}</div>
                <div className={styles.headerText}>{handleDistance()}</div>
              </div>
            </div>
          </div>

          <div className={styles.tabContent}>
            <SelectLayer
              index={0}
              buttons={hazardButtons}
              selectedButtons={selectedButtons}
              setSelectedButtons={setSelectedButtons}
              type="shape"
            />
          </div>
        </div>
        <div className="flex justify-between mt-5 mb-5">
          <div>
            <Button onClick={handleRevert} variant="text" color="success">
              {t("buttons.reset")}
            </Button>
          </div>
          <div>
            <Button onClick={close} variant="text" color="success">
              {t("buttons.close")}
            </Button>
            <Button
              color="success"
              variant="contained"
              disabled={!isAnySelected}
              onClick={() => onOpen()}
            >
              {t("buttons.runAnalysis")}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default AnalyseShapeModal;
